import React from "react";
import { Table, Button, Form } from "react-bootstrap";
import { FaEye, FaToggleOff, FaToggleOn, FaTrash, FaUndo } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import UserInitialsAvatar from "../../../utils/userInitialAvatar";
import { getFullName } from "../../../utils/helpers";
import { getProfilePictureUrl } from "../../../utils/imageUtils";

const EmployeeTable = ({
  users = [],
  selectedRowIds,
  allVisibleSelected,
  toggleSelectAllVisible,
  toggleSelectRow,
  getDelegationName,
  timeAgo,
  onOpenUserPage,
  onViewUser,
  onDeleteUser,
  onToggleStatus,
  onRestoreUser,
  canManageUsers = true,
  canViewSensitive = true,
}) => {
  const { t } = useTranslation();
  const columnCount = 5 + (canViewSensitive ? 2 : 0) + (canManageUsers ? 2 : 0);

  return (
    <Table hover responsive className="mb-0 align-middle">
      <thead style={{ borderBottom: "var(--border-thin)" }}>
        <tr>
          {canManageUsers && (
            <th className="py-3 px-4 border-0" style={{ width: 40 }} onClick={(e) => e.stopPropagation()}>
              <Form.Check
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
              />
            </th>
          )}
          <th className="txt-subtle fw-semibold py-3 px-4 border-0">
            {t("components.EmployeeTable.employee")}
          </th>
          <th className="txt-subtle fw-semibold py-3 px-4 border-0">
            {t("components.EmployeeTable.role")}
          </th>
          <th className="txt-subtle fw-semibold py-3 px-4 border-0">
            {t("components.EmployeeTable.workplace")}
          </th>
          <th className="txt-subtle fw-semibold py-3 px-4 border-0">
            {t("components.EmployeeTable.location", "Location")}
          </th>
          {canViewSensitive && (
            <th className="txt-subtle fw-semibold py-3 px-4 border-0">
              {t("components.EmployeeTable.hiring")}
            </th>
          )}
          {canViewSensitive && (
            <th className="txt-subtle fw-semibold py-3 px-4 border-0">
              {t("components.EmployeeTable.email")}
            </th>
          )}
          {canManageUsers && (
            <th className="txt-subtle fw-semibold py-3 px-4 border-0">
              {t("components.EmployeeTable.status", "Status")}
            </th>
          )}
          <th className="txt-subtle fw-semibold py-3 px-4 border-0 text-center">
            {t("components.EmployeeTable.actions")}
          </th>
        </tr>
      </thead>

      <tbody>
        {users.length > 0 ? (
          users.map((user) => {
            const pic = getProfilePictureUrl(user);
            // Deleted users can't be opened (the API 404s), only restored.
            const isDeleted = Boolean(user.deleted_at);

            return (
              <tr
                key={user.id}
                data-deleted={isDeleted || undefined}
                style={{
                  borderBottom: "var(--border-thin)",
                  cursor: isDeleted ? "default" : "pointer",
                  ...(isDeleted && { "--bs-table-bg": "rgba(239, 68, 68, 0.08)", opacity: 0.75 }),
                }}
                onClick={isDeleted ? undefined : () => onOpenUserPage?.(user)}
              >
                {canManageUsers && (
                  <td
                    className="py-3 px-4 border-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={selectedRowIds.has(user.id)}
                      disabled={isDeleted}
                      onChange={() => toggleSelectRow(user.id)}
                    />
                  </td>
                )}

                <td className="py-3 px-4 border-0">
                  <div className="d-flex align-items-center">
                    {pic ? (
                      <img
                        src={pic}
                        alt={user.username}
                        className="me-3 shadow-sm"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div className="me-3">
                        <UserInitialsAvatar name={user.username} />
                      </div>
                    )}
                    <span className="fw-bold txt-main">
                      {getFullName(user)}
                    </span>
                  </div>
                </td>

                <td className="py-3 px-4 txt-subtle border-0">
                  {user.role?.name || t("components.EmployeeTable.na")}
                </td>

                <td className="py-3 px-4 txt-subtle border-0">
                  {getDelegationName(user)}
                </td>

                <td className="py-3 px-4 txt-subtle border-0">
                  {[user.city, user.country].filter(Boolean).join(", ") || t("components.EmployeeTable.na")}
                </td>

                {canViewSensitive && (
                  <td className="py-3 px-4 txt-subtle border-0">
                    {timeAgo(user.hiring_date || user.created_at)}
                  </td>
                )}

                {canViewSensitive && (
                  <td className="py-3 px-4 txt-subtle border-0">{user.email}</td>
                )}

                {canManageUsers && (
                  <td className="py-3 px-4 border-0">
                    {isDeleted ? (
                      <span className="badge bg-danger">
                        {t("components.EmployeeTable.deleted", "Deleted")}
                      </span>
                    ) : (
                      <span className={`badge ${user.is_active ? "bg-success" : "bg-secondary"}`}>
                        {user.is_active
                          ? t("components.EmployeeTable.active", "Active")
                          : t("components.EmployeeTable.inactive", "Inactive")}
                      </span>
                    )}
                  </td>
                )}

                <td className="py-3 px-4 text-center border-0" onClick={(e) => e.stopPropagation()}>
                  <div className="d-flex justify-content-center align-items-center gap-3">
                    {isDeleted ? (
                      canManageUsers && (
                        <Button
                          variant="link"
                          className="p-0 m-0 action-btn-icon"
                          aria-label={t("components.EmployeeTable.restore", "Restore")}
                          title={t("components.EmployeeTable.restore", "Restore")}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestoreUser?.(user);
                          }}
                        >
                          <FaUndo size={16} />
                        </Button>
                      )
                    ) : (
                    <>
                    <Button
                      variant="link"
                      className="p-0 m-0 txt-subtle action-btn-icon"
                      aria-label={t("components.EmployeeTable.view", "View")}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewUser?.(user);
                      }}
                    >
                      <FaEye size={20} />
                    </Button>
                    {canManageUsers && (
                      <Button
                        variant="link"
                        className="p-0 m-0 action-btn-icon"
                        aria-label={
                          user.is_active
                            ? t("components.EmployeeTable.deactivate", "Deactivate")
                            : t("components.EmployeeTable.activate", "Activate")
                        }
                        title={
                          user.is_active
                            ? t("components.EmployeeTable.deactivate", "Deactivate")
                            : t("components.EmployeeTable.activate", "Activate")
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleStatus?.(user);
                        }}
                      >
                        {user.is_active ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                      </Button>
                    )}
                    {canManageUsers && (
                      <Button
                        variant="link"
                        className="p-0 m-0 text-danger action-btn-icon"
                        aria-label={t("components.EmployeeTable.delete", "Delete")}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteUser?.(user);
                        }}
                      >
                        <FaTrash size={18} />
                      </Button>
                    )}
                    </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })
        ) : (
          <tr>
            <td colSpan={columnCount} className="text-center py-4">
              {t("components.EmployeeTable.noEmployeesFound")}
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default EmployeeTable;